import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Video, Calendar, Clock, User, Phone, Mail, Briefcase } from 'lucide-react';
import { apiClient } from '@/shared/services/apiClient';
import { format } from 'date-fns';

interface CallEvent {
  id: string;
  title: string;
  start_time: string;
  end_time?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  project?: string;
  room_id?: string;
  join_link?: string;
  call_link?: string;
  ended_at?: string | null;  // ✅ NEW: Track if call session has ended
}

export function CallNotificationModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [callEvent, setCallEvent] = useState<CallEvent | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const eventId = searchParams.get('event');

    if (eventId) {
      setLoading(true);
      loadCallEvent(eventId);
    } else {
      setIsOpen(false);
      setCallEvent(null);
    }
  }, [searchParams]);

  const loadCallEvent = async (eventId: string) => {
    try {
      // CRITICAL FIX: Use localStorage instead of getUser() to prevent hanging
      const { getUserId } = await import('@/shared/utils/supabaseStorage');
      const userId = getUserId();
      if (!userId) {
        console.error('[CallNotificationModal] No user found');
        setLoading(false);
        return;
      }

      // SECURITY: Fetch event via backend API
      const response = await apiClient.get<{ ok: boolean; event: any }>(
        `/calendar/events/${eventId}`
      );

      if (!response.ok || !response.event) {
        console.error('[CallNotificationModal] Error loading event');
        setLoading(false);
        return;
      }

      const data = response.event;
      console.log('[CallNotificationModal] Loaded event:', data);

      // ✅ Check if there's a related call_session with ended_at
      let callEndedAt = null;
      if (data.room_id) {
        // SECURITY: Use backend API instead of direct Supabase call
        try {
          const sessionResponse = await apiClient.get<{ ok: boolean; session: { ended_at: string | null; status: string } | null }>(
            `/session/${data.room_id}/status`
          );

          if (sessionResponse.ok && sessionResponse.session) {
            callEndedAt = sessionResponse.session.ended_at;
            console.log('[CallNotificationModal] Call session status:', {
              status: sessionResponse.session.status,
              ended_at: sessionResponse.session.ended_at
            });
          }
        } catch (sessionErr) {
          console.warn('[CallNotificationModal] Could not fetch session status:', sessionErr);
        }
      }

      setCallEvent({ ...data, ended_at: callEndedAt });
      setIsOpen(true);
      setLoading(false);
    } catch (err) {
      console.error('[CallNotificationModal] Error:', err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Clear the event query parameter
    setSearchParams({});
    setCallEvent(null);
  };

  const handleJoinCall = () => {
    if (!callEvent) {
      console.error('[CallNotificationModal] No call event - cannot join');
      return;
    }

    // Build the call link
    // CRITICAL: Hosts should ALWAYS go through prejoin lobby with /join/:roomId?host=1
    // ALWAYS generate from room_id to ensure correct path (ignore old database links with /call/)
    const callLink = callEvent.room_id ? `/join/${callEvent.room_id}?host=1` : null;

    console.log('[CallNotificationModal] Join button clicked:', {
      roomId: callEvent.room_id,
      callLink,
      callEvent
    });

    if (callLink) {
      console.log('[CallNotificationModal] ✅ Navigating to prejoin:', callLink);

      // Close modal first
      handleClose();

      // Then navigate
      // If it's a full URL, open in new tab
      if (callLink.startsWith('http')) {
        window.open(callLink, '_blank');
      } else {
        // Otherwise navigate
        setTimeout(() => {
          navigate(callLink);
        }, 100); // Small delay to ensure modal closes first
      }
    } else {
      console.error('[CallNotificationModal] ❌ No call link available - room_id missing');
    }
  };

  const handleViewInCalendar = () => {
    handleClose();
    navigate('/calendar');
  };

  const handleCancelCall = async () => {
    if (!callEvent) return;

    try {
      // SECURITY: Use backend API to delete calendar event
      await apiClient.delete(`/calendar/events/${callEvent.id}`);

      console.log('[CallNotificationModal] Call cancelled successfully');
      handleClose();

      // Refresh calendar if on calendar page
      window.dispatchEvent(new Event('calendar:refresh'));
    } catch (err) {
      console.error('[CallNotificationModal] Error cancelling call:', err);
    }
  };

  if (!callEvent) return null;

  const startTime = new Date(callEvent.start_time);
  const now = new Date();
  const minutesUntilStart = Math.floor((startTime.getTime() - now.getTime()) / 60000);
  const isStartingSoon = minutesUntilStart <= 5 && minutesUntilStart >= -5;

  // Show Join/Cancel buttons if:
  // - Within 15 minutes BEFORE the scheduled start time, OR
  // - Within 5 minutes AFTER the scheduled start time
  // - AND the call hasn't ended
  const minutesSinceStart = -minutesUntilStart;
  const isCallActive = (minutesUntilStart <= 15 && minutesUntilStart >= 0) || (minutesSinceStart <= 5 && minutesSinceStart >= 0);
  const canJoin = isCallActive && !callEvent.ended_at;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            {callEvent.title || 'Scheduled Call'}
          </DialogTitle>
          <DialogDescription>
            {isStartingSoon ? (
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                Your call is starting {minutesUntilStart > 0 ? `in ${minutesUntilStart} minutes` : 'now'}!
              </span>
            ) : (
              <span>
                Scheduled for {format(startTime, 'EEEE, MMMM d')} at {format(startTime, 'h:mm a')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Call Details */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <div className="font-medium">
                  {format(startTime, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="text-sm text-muted-foreground">
                  {format(startTime, 'h:mm a')}
                  {callEvent.end_time && ` - ${format(new Date(callEvent.end_time), 'h:mm a')}`}
                </div>
              </div>
            </div>

            {callEvent.customer_name && (
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">{callEvent.customer_name}</div>
                </div>
              </div>
            )}

            {callEvent.customer_phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  {callEvent.customer_phone}
                </div>
              </div>
            )}

            {callEvent.customer_email && (
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  {callEvent.customer_email}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {canJoin ? (
            <div className="flex flex-col gap-3 pt-4">
              <Button
                onClick={handleJoinCall}
                className="w-full gap-2"
                size="lg"
                disabled={!callEvent.room_id && !callEvent.join_link && !callEvent.call_link}
              >
                <Video className="h-4 w-4" />
                Join Call Now
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={handleViewInCalendar}
                  variant="outline"
                  className="flex-1 gap-2"
                  size="lg"
                >
                  <Calendar className="h-4 w-4" />
                  View in Calendar
                </Button>
                <Button
                  onClick={handleCancelCall}
                  variant="destructive"
                  className="flex-1 gap-2"
                  size="lg"
                >
                  Cancel Call
                </Button>
              </div>
            </div>
          ) : (
            <div className="pt-4">
              {callEvent.ended_at ? (
                <div className="space-y-3">
                  <div className="bg-muted/50 border border-border rounded-lg p-4 text-center">
                    <p className="font-medium text-sm mb-1">This call has ended</p>
                    <p className="text-xs text-muted-foreground">
                      Ended {format(new Date(callEvent.ended_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/calls')}
                    variant="outline"
                    className="w-full gap-2"
                    size="lg"
                  >
                    View in Call History
                  </Button>
                </div>
              ) : (
                <div>
                  <Button
                    onClick={handleViewInCalendar}
                    variant="outline"
                    className="w-full gap-2"
                    size="lg"
                  >
                    <Calendar className="h-4 w-4" />
                    View in Calendar
                  </Button>
                  <p className="text-sm text-muted-foreground text-center mt-3">
                    This call has passed. Join and cancel are no longer available.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
